import React, { useState, useMemo } from "react";
import {
  Calculator,
  TrendingUp,
  DollarSign,
  PlusCircle,
  Search,
  ClipboardCheck,
  Users,
  X,
  ArrowRight,
} from "lucide-react";
import AppLayout from "../Layout/AppLayout";
import ActionPlanChecklist, { SectionLabel } from "./ActionPlanChecklist";
import FeaturedPartnerCard from "../shared/FeaturedPartnerCard";
import DealListingMiniGrid from "../DealRoom/DealListingMiniGrid";
import CompleteProfileBanner from "./CompleteProfileBanner";
import SuccessionTeamBlock from "./SuccessionTeamBlock";
import PreAssessmentHero from "./PreAssessmentHero";

const categoryColors = {
  financial: "hsl(var(--chart-1))",
  human: "hsl(var(--chart-2))",
  structural: "hsl(var(--chart-3))",
  customer: "hsl(var(--chart-4))",
  wealth: "hsl(var(--chart-5))",
};

const sellerTools = [
  {
    icon: TrendingUp,
    title: "Valuation Studio",
    description: "Estimate what your business is worth",
    ctaText: "Get Your Number",
    link: "/succeedo/valuation",
  },
  {
    icon: Calculator,
    title: "Deal Structure Tool",
    description: "Model your sale options",
    ctaText: "Explore Structures",
    link: "/succeedo/deal-structure",
  },
  {
    icon: DollarSign,
    title: "Financing Calculator",
    description: "Run the numbers on your deal",
    ctaText: "Run Scenarios",
    link: "/succeedo/financing-calculator",
  },
  {
    icon: PlusCircle,
    title: "Create a Listing",
    description: "List your business for sale",
    ctaText: "Create Listing",
  },
];

const buyerTools = [
  {
    icon: Search,
    title: "Browse Listings",
    description: "Find businesses for sale",
    ctaText: "Browse Now",
  },
  {
    icon: ClipboardCheck,
    title: "Buyer Readiness Profile",
    description: "Assess your acquisition readiness",
    ctaText: "Build Profile",
  },
  {
    icon: DollarSign,
    title: "Financing Calculator",
    description: "Model your acquisition financing",
    ctaText: "Run Scenarios",
    link: "/succeedo/financing-calculator",
  },
  {
    icon: TrendingUp,
    title: "Valuation Studio",
    description: "Validate what a business is worth",
    ctaText: "Analyze a Business",
  },
  {
    icon: Users,
    title: "Partner Directory",
    description: "Find your deal team",
    ctaText: "Find Partners",
  },
];

/* ------------------------------------------------------------------ */
/*  Tool Card                                                         */
/* ------------------------------------------------------------------ */
function ToolCard({ icon: Icon, title, description, ctaText, link, lastActivity }) {
  if (lastActivity) {
    return (
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6 hover:border-[hsl(var(--border))]/50 transition-colors group">
        <div className="flex items-center gap-3 mb-3">
          <Icon className="w-5 h-5 text-muted-foreground" strokeWidth={2} />
          <h3 className="text-[hsl(var(--foreground))] font-semibold text-base">{title}</h3>
        </div>
        <div className="text-[20px] font-semibold text-[hsl(var(--foreground))] mb-2">
          {lastActivity.valuationRange}
        </div>
        <div className="flex items-center gap-2 mb-4">
          <span className="text-[12px] text-muted-foreground">{lastActivity.date}</span>
          {lastActivity.industry && (
            <span className="font-mono text-[10px] uppercase border border-[hsl(var(--border))] rounded-full px-2 py-0.5 text-muted-foreground">
              {lastActivity.industry}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <a href={lastActivity.viewResultsLink} className="bg-[hsl(var(--foreground))] text-[hsl(var(--background))] rounded-full px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity">
            {lastActivity.viewLabel || 'View Results'}
          </a>
          <a href={lastActivity.newLink} className="border border-[hsl(var(--border))] text-[hsl(var(--foreground))] rounded-full px-4 py-2 text-sm hover:bg-[hsl(var(--accent))] transition-colors">
            {lastActivity.newLabel || 'New'}
          </a>
        </div>
      </div>
    );
  }

  const inner = (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6 hover:border-[hsl(var(--border))]/50 transition-colors group">
      <Icon className="w-5 h-5 text-muted-foreground mb-4" strokeWidth={2} />
      <h3 className="text-[hsl(var(--foreground))] font-semibold text-base mb-2">
        {title}
      </h3>
      <p className="text-muted-foreground text-sm mb-4">{description}</p>
      <span className="inline-block bg-[hsl(var(--foreground))] text-[hsl(var(--background))] rounded-full px-5 py-2 text-sm font-medium hover:opacity-90 transition-opacity">
        {ctaText}
      </span>
    </div>
  );

  if (link) {
    return <a href={link}>{inner}</a>;
  }

  return inner;
}

/* ------------------------------------------------------------------ */
/*  First Visit Banner                                                */
/* ------------------------------------------------------------------ */
function FirstVisitBanner({ onDismiss }) {
  return (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] border-l-2 border-l-[hsl(var(--succeedo-orange))] rounded-[16px] p-5 mb-8">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="inline-flex items-center bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))]/20 text-[hsl(var(--succeedo-orange))] rounded-full px-3 py-1 mb-3">
            <span className="font-mono text-[11px] uppercase tracking-wider">
              ASSESSMENT AVAILABLE
            </span>
          </div>
          <h3 className="text-[hsl(var(--foreground))] font-semibold text-base mb-2">
            Get your personalized 90-day action plan
          </h3>
          <p className="text-muted-foreground text-sm mb-4">
            Take our 10-minute Common Sense Scoring&trade; assessment &mdash;
            your red flags become your 90-day action plan automatically.
          </p>
          <a
            href="/succeedo/assessment"
            className="inline-block bg-[hsl(var(--succeedo-orange))] text-[hsl(var(--background))] rounded-full px-5 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Start Assessment
          </a>
        </div>
        <button
          onClick={onDismiss}
          className="text-muted-foreground hover:text-[hsl(var(--foreground))] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tool Grid                                                         */
/* ------------------------------------------------------------------ */
function ToolGrid({ tools }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {tools.map((tool, index) => (
        <ToolCard key={index} {...tool} />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sprint Expired Banner                                             */
/* ------------------------------------------------------------------ */
function SprintExpiredBanner() {
  return (
    <div className="bg-card border border-border border-l-2 border-l-[hsl(var(--succeedo-orange))] rounded-[16px] p-5 mb-8">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="inline-flex items-center bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))]/20 text-[hsl(var(--succeedo-orange))] rounded-full px-3 py-1 mb-3">
            <span className="font-mono text-[11px] uppercase tracking-wider">
              90-DAY PLAN COMPLETE
            </span>
          </div>
          <h3 className="text-foreground font-semibold text-base mb-2">
            Your 90-day plan has ended — let&apos;s see how you&apos;ve improved
          </h3>
          <p className="text-muted-foreground text-sm mb-4">
            Retake your Common Sense Scoring&trade; assessment to measure your progress,
            unlock new action items, and connect with the right partners for your next phase.
          </p>
          <a
            href="/succeedo/assessment"
            className="inline-block bg-[hsl(var(--succeedo-orange))] text-background rounded-full px-5 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Retake Assessment
          </a>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Summary Strip — 3 compact cards (Assessment / Value Gap / Sprint) */
/* ------------------------------------------------------------------ */
function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function getTierColor(tier) {
  switch (tier) {
    case "red_flag":
      return "bg-red-500/10 border-red-500/20 text-red-400";
    case "average":
      return "bg-amber-500/10 border-amber-500/20 text-amber-400";
    case "green_zone":
      return "bg-green-500/10 border-green-500/20 text-green-400";
    case "best_in_class":
      return "bg-[hsl(var(--chart-2))]/10 border-[hsl(var(--chart-2))]/20 text-[hsl(var(--chart-2))]";
    default:
      return "border-[hsl(var(--border))] text-muted-foreground";
  }
}

function tierLabel(tier) {
  switch (tier) {
    case "red_flag":
      return "RED FLAG";
    case "average":
      return "AVERAGE";
    case "green_zone":
      return "GREEN ZONE";
    case "best_in_class":
      return "BEST-IN-CLASS";
    default:
      return tier ? tier.replace(/_/g, " ").toUpperCase() : "";
  }
}

function SummaryStrip({ scorecard }) {
  if (!scorecard || !scorecard.assessment) return null;

  const { assessment, valuation, sprint } = scorecard;
  const rawScore = assessment.score || 0;
  const score = Math.round(rawScore <= 1 ? rawScore * 100 : rawScore);
  const tier = assessment.tier;
  const hasValuation = !!valuation;

  const sprintCompleted = sprint?.completed || 0;
  const sprintTotal = sprint?.total || 0;
  const daysRemaining = sprint?.days_remaining;
  const progressPercentage = sprintTotal > 0 ? (sprintCompleted / sprintTotal) * 100 : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
      {/* Card 1: Assessment Score */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[12px] p-4">
        <div className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide mb-3">
          ASSESSMENT
        </div>
        <div className="flex items-center gap-3 mb-2">
          <div className="text-[28px] font-light text-[hsl(var(--foreground))]">
            {score}%
          </div>
          {tier && (
            <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase border ${getTierColor(tier)}`}>
              {tierLabel(tier)}
            </span>
          )}
        </div>
        <a
          href="/succeedo/action-plan"
          className="text-[13px] text-[hsl(var(--succeedo-orange))] hover:underline"
        >
          View details &rarr;
        </a>
      </div>

      {/* Card 2: Value Gap */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[12px] p-4">
        {hasValuation ? (
          <>
            <div className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide mb-3">
              VALUE GAP
            </div>
            <div className="text-[28px] font-light text-[hsl(var(--succeedo-orange))] mb-1">
              {formatCurrency(valuation.value_gap || 0)}
            </div>
            <div className="text-[12px] text-muted-foreground mb-2">
              {formatCurrency(valuation.current_value || 0)} &rarr; {formatCurrency(valuation.potential_value || 0)} potential
            </div>
            <a
              href="/succeedo/action-plan"
              className="text-[13px] text-[hsl(var(--succeedo-orange))] hover:underline"
            >
              Close the gap &rarr;
            </a>
          </>
        ) : (
          <>
            <div className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide mb-3">
              RUN VALUATION
            </div>
            <div className="text-[14px] text-muted-foreground mb-3">
              Discover your value gap
            </div>
            <a
              href="/succeedo/valuation"
              className="text-[13px] text-[hsl(var(--succeedo-orange))] hover:underline"
            >
              Get started &rarr;
            </a>
          </>
        )}
      </div>

      {/* Card 3: Sprint Progress */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[12px] p-4">
        <div className="font-mono text-[10px] uppercase text-muted-foreground tracking-wide mb-3">
          90-DAY PLAN
        </div>
        <div className="text-[28px] font-light text-[hsl(var(--foreground))] mb-1">
          {sprintCompleted} of {sprintTotal}
        </div>
        <div className="text-[12px] text-muted-foreground mb-3">complete</div>
        <div className="h-1.5 bg-[hsl(var(--border))] rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-gradient-to-r from-[hsl(var(--succeedo-orange))] to-[hsl(var(--succeedo-orange))]/70 transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        {daysRemaining != null && (
          <div className="text-[11px] text-muted-foreground">
            {daysRemaining} days remaining
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Top 3 Action Items                                                */
/* ------------------------------------------------------------------ */
function formatImpact(value) {
  if (value >= 1000000) return `+$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `+$${Math.round(value / 1000)}K`;
  return `+$${value}`;
}

function getStatusStyles(status) {
  switch (status) {
    case "in_progress":
      return "bg-[hsl(var(--succeedo-orange))]/10 text-[hsl(var(--succeedo-orange))] border-[hsl(var(--succeedo-orange))]/20";
    default:
      return "border-[hsl(var(--border))] text-muted-foreground";
  }
}

function statusLabel(status) {
  switch (status) {
    case "in_progress":
      return "IN PROGRESS";
    case "completed":
      return "DONE";
    default:
      return "OPEN";
  }
}

function TopActionItems({ items }) {
  if (!items || items.length === 0) return null;

  const top3 = items.filter((i) => i.status !== "completed").slice(0, 3);
  if (top3.length === 0) return null;

  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))] shadow-[0_0_8px_hsl(var(--succeedo-orange))]" />
        <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          YOUR TOP PRIORITIES
        </span>
      </div>

      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[12px] divide-y divide-[hsl(var(--border))]">
        {top3.map((item) => {
          const cat = (item.partner_category || "").toLowerCase();
          const dotColor = categoryColors[cat] || "hsl(var(--muted-foreground))";
          const impact = item.metadata?.value_impact;

          return (
            <a key={item.id} href="/succeedo/action-plan" className="p-4 flex items-center gap-4 hover:bg-muted/30 transition-colors block">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: dotColor }}
                />
                <div className="text-[14px] text-[hsl(var(--foreground))] truncate">
                  {item.title}
                </div>
              </div>

              {impact != null && impact > 0 && (
                <div className="text-[hsl(var(--chart-2))] font-mono text-[11px] shrink-0">
                  {formatImpact(impact)}
                </div>
              )}

              <div
                className={`px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-wide border shrink-0 ${getStatusStyles(item.status)}`}
              >
                {statusLabel(item.status)}
              </div>
            </a>
          );
        })}
      </div>

      <div className="mt-3 text-center">
        <a
          href="/succeedo/action-plan"
          className="text-[13px] text-[hsl(var(--succeedo-orange))] hover:underline"
        >
          View full action plan &rarr;
        </a>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Dashboard                                                    */
/* ------------------------------------------------------------------ */
export default function SucceedoDashboard({
  persona,
  userName,
  userInitials,
  actionPlanItems,
  openCount,
  completedCount,
  daysRemaining,
  sprintExpired,
  onStatusChange,
  featuredPartners = [],
  onRequestIntroduction,
  introductionRequests = [],
  introLoading = false,
  lastValuationSnapshot,
  lastFinancingSnapshot,
  lastDealStructureSnapshot,
  scorecard,
  profileIncompleteSections = [],
  advisoryTeam = [],
  hasAssessment = false,
  onAddAdvisor,
  onFindPartner,
}) {
  const [bannerDismissed, setBannerDismissed] = useState(
    () => localStorage.getItem("succeedo_banner_dismissed") === "true"
  );

  const [profileBannerDismissed, setProfileBannerDismissed] = useState(
    () => localStorage.getItem("succeedo_profile_banner_dismissed") === "true"
  );

  const handleDismissProfileBanner = () => {
    localStorage.setItem("succeedo_profile_banner_dismissed", "true");
    setProfileBannerDismissed(true);
  };

  const totalCount = openCount + completedCount;
  const planProgress = useMemo(
    () => ({
      completed: completedCount,
      total: totalCount,
      percentage: totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0,
    }),
    [completedCount, totalCount]
  );

  const handleDismissBanner = () => {
    localStorage.setItem("succeedo_banner_dismissed", "true");
    setBannerDismissed(true);
  };

  const isBuyer = persona === "buyer";
  const isSeller = persona === "seller" || persona === "both";
  const hasActionPlanItems = actionPlanItems && actionPlanItems.length > 0;
  const hasScorecard = !!scorecard && !!scorecard.assessment;

  // Build tool card return state for Valuation Studio
  // Prefer CEPA-adjusted single value from scorecard; fall back to raw range
  const valuationActivity = useMemo(() => {
    if (!lastValuationSnapshot) return null;
    const formatVal = (v) => {
      if (!v) return "$0";
      const num = typeof v === "string" ? parseFloat(v) : v;
      if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
      if (num >= 1_000) return `$${Math.round(num / 1_000)}K`;
      return `$${Math.round(num).toLocaleString()}`;
    };
    const date = lastValuationSnapshot.created_at
      ? new Date(lastValuationSnapshot.created_at).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";
    const sector = (lastValuationSnapshot.sector_cluster || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

    // CEPA-adjusted value from scorecard (primary snapshot + assessment)
    const cepaValue = scorecard?.valuation?.current_value;
    const range = lastValuationSnapshot.value_range || {};
    const displayValue = cepaValue != null
      ? formatVal(cepaValue)
      : `${formatVal(range.low)} \u2014 ${formatVal(range.high)}`;

    return {
      valuationRange: displayValue,
      date,
      industry: sector,
      viewResultsLink: "/succeedo/valuation",
      newLink: "/succeedo/valuation?new=1",
      viewLabel: "View Results",
      newLabel: "New Valuation",
    };
  }, [lastValuationSnapshot, scorecard]);

  // Build tool card return state for Financing Calculator
  const financingActivity = useMemo(() => {
    if (!lastFinancingSnapshot) return null;
    const formatVal = (v) => {
      if (!v) return "$0";
      const num = typeof v === "string" ? parseFloat(v) : v;
      if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
      if (num >= 1_000) return `$${Math.round(num / 1_000)}K`;
      return `$${Math.round(num).toLocaleString()}`;
    };
    const payment = lastFinancingSnapshot.monthly_payment || lastFinancingSnapshot.results?.monthly_payment;
    const lender = lastFinancingSnapshot.lender_name || '';
    const date = lastFinancingSnapshot.created_at
      ? new Date(lastFinancingSnapshot.created_at).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";

    return {
      valuationRange: `${formatVal(payment)}/mo`,
      date,
      industry: lender,
      viewResultsLink: "/succeedo/financing-calculator",
      newLink: "/succeedo/financing-calculator",
      viewLabel: "View Scenario",
      newLabel: "New Scenario",
    };
  }, [lastFinancingSnapshot]);

  // Build tool card return state for Deal Structure Tool
  const dealStructureActivity = useMemo(() => {
    if (!lastDealStructureSnapshot) return null;
    const formatVal = (v) => {
      if (!v) return "$0";
      const num = typeof v === "string" ? parseFloat(v) : v;
      if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
      if (num >= 1_000) return `$${Math.round(num / 1_000)}K`;
      return `$${Math.round(num).toLocaleString()}`;
    };
    const results = lastDealStructureSnapshot.results || {};
    const afterTax = results.after_tax_proceeds;
    const structureType = (lastDealStructureSnapshot.structure_type || results.structure_type || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    const date = lastDealStructureSnapshot.created_at
      ? new Date(lastDealStructureSnapshot.created_at).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";

    return {
      valuationRange: `${formatVal(afterTax)} after tax`,
      date,
      industry: structureType,
      viewResultsLink: "/succeedo/deal-structure",
      newLink: "/succeedo/deal-structure",
      viewLabel: "View Structure",
      newLabel: "New Structure",
    };
  }, [lastDealStructureSnapshot]);

  // Inject return state into tool cards
  const enrichTool = (tool) => {
    if (tool.title === "Valuation Studio" && valuationActivity) return { ...tool, lastActivity: valuationActivity };
    if (tool.title === "Deal Structure Tool" && dealStructureActivity) return { ...tool, lastActivity: dealStructureActivity };
    if (tool.title === "Financing Calculator" && financingActivity) return { ...tool, lastActivity: financingActivity };
    return tool;
  };

  const enrichedSellerTools = useMemo(
    () => sellerTools.map(enrichTool),
    [valuationActivity, dealStructureActivity, financingActivity]
  );

  const enrichedBuyerTools = useMemo(
    () => buyerTools.map(enrichTool),
    [valuationActivity, dealStructureActivity, financingActivity]
  );

  // PreAssessmentHero subsumes the FirstVisitBanner CTA for fresh sellers,
  // so suppress the banner when the hero will render to avoid stacking
  // two "take the assessment" surfaces on the same page.
  const isFreshSeller = !hasScorecard && advisoryTeam.every((m) => !m.status || m.status === 'not_added');
  const showBanner = isSeller && !hasActionPlanItems && !bannerDismissed && !isFreshSeller;

  return (
    <AppLayout
      breadcrumb="DASHBOARD"
      persona={persona}
      userName={userName}
      userInitials={userInitials}
      planProgress={planProgress}
      actionPlanBadge={openCount}
    >
      <div className="p-6">
        {isBuyer && (
          <>
            <SectionLabel text="BUYER TOOLS" />
            <ToolGrid tools={enrichedBuyerTools} />
          </>
        )}

        {isSeller && (
          <>
            {sprintExpired && <SprintExpiredBanner />}
            {showBanner && !sprintExpired && (
              <FirstVisitBanner onDismiss={handleDismissBanner} />
            )}

            {/* Pre-assessment fresh-seller hero (Unit 8) — shown when the
                seller has neither an assessment nor any advisory team
                members yet. Replaces the empty SummaryStrip slot. */}
            {isFreshSeller && <PreAssessmentHero />}

            {/* Summary Strip — shows after assessment */}
            {hasScorecard && <SummaryStrip scorecard={scorecard} />}

            {/* Succession Team Block — always visible for sellers,
                state-aware copy. Below the hero, above Top Priorities. */}
            <SuccessionTeamBlock
              members={advisoryTeam}
              hasAssessment={hasAssessment}
              onAddMine={onAddAdvisor}
              onFindOne={onFindPartner}
            />

            {/* Complete Profile Banner — shows when onboarding steps were skipped */}
            {profileIncompleteSections.length > 0 && !profileBannerDismissed && (
              <CompleteProfileBanner
                incompleteSections={profileIncompleteSections}
                onDismiss={handleDismissProfileBanner}
              />
            )}

            {/* Top 3 Action Items — compact, non-expandable */}
            {hasActionPlanItems && <TopActionItems items={actionPlanItems} />}

            <SectionLabel text="SELLER TOOLS" />
            <ToolGrid tools={enrichedSellerTools} />
          </>
        )}
        {/* Featured Partners */}
        {/* Deal Room mini-grid (S3-B) — buyer-flavored content. Hidden
            from sellers so the dashboard stays focused on their own
            team, action plan, and listing status rather than other
            sellers' competitive listings. */}
        {!isSeller && (
          <div className="mt-12">
            <DealListingMiniGrid />
          </div>
        )}

        {featuredPartners.length > 0 && (
          <div className="mt-12">
            <div className="flex items-center justify-between mb-4">
              <SectionLabel text="FEATURED PARTNERS" />
              <a href="/succeedo/partners" className="text-[13px] text-[hsl(var(--succeedo-orange))] hover:underline flex items-center gap-1">
                View all partners <ArrowRight className="w-3 h-3" />
              </a>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {featuredPartners.map((p) => (
                <FeaturedPartnerCard key={p.id} partner={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
